import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import AfasArticlePiP from './AfasArticlePiP';
import { CAP } from '../utils/permissions';
import { usePermissions, ReadOnlyNotice } from '../utils/PermissionContext';
import { apiFetch } from '../utils/apiAuth';
import { ADMINISTRATIE_XEBIA_CLOUD_CONTROL } from '../utils/administraties';
import { useSortConfig } from '../hooks/useSortConfig';
import { useCloudRunJob } from '../hooks/useCloudRunJob';

const AdminFlexiblePlans = ({ googleIdToken, t }) => {
  const { can } = usePermissions();
  const canWrite = can(CAP.ADMIN_WRITE);
  const { startJob } = useCloudRunJob();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, handleSort] = useSortConfig({ key: 'domain_name', direction: 'asc' });
  const [selectedPlans, setSelectedPlans] = useState(new Set());
  const [sendingToAfas, setSendingToAfas] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showAfasPiP, setShowAfasPiP] = useState(false);
  const [afasArticles, setAfasArticles] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    now.setDate(1); // Set to 1st to avoid rollover issues when subtracting a month
    now.setMonth(now.getMonth() - 1);
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showFiltersModal, setShowFiltersModal] = useState(false);
  const [selectedAfasCodes, setSelectedAfasCodes] = useState(new Set());
  const [selectedDomains, setSelectedDomains] = useState(new Set());

  const uniqueAfasCodes = useMemo(() => {
    const codes = new Set(plans.map(p => p.afas_artikelcode).filter(Boolean));
    return Array.from(codes).sort();
  }, [plans]);

  const uniqueDomains = useMemo(() => {
    const domains = new Set(plans.map(p => p.domain_name).filter(Boolean));
    return Array.from(domains).sort();
  }, [plans]);

  const apiUrl = process.env.REACT_APP_API_URL || '';

  useEffect(() => {
    const fetchPlans = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await apiFetch(`${apiUrl}/api/admin/flexible-plans?month=${selectedMonth}`);

        if (!response.ok) {
          throw new Error('Failed to fetch flexible plans data');
        }

        const data = await response.json();
        setPlans(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    const fetchAfasArticles = async () => {
      try {
        const response = await apiFetch(`${apiUrl}/api/afas-articles`);
        if (response.ok) {
          const data = await response.json();
          setAfasArticles(data);
        }
      } catch (err) {
        console.error("Failed to fetch AFAS articles", err);
      }
    };

    if (googleIdToken) {
      fetchPlans();
      fetchAfasArticles();
    }
  }, [apiUrl, googleIdToken, selectedMonth]);


  const toggleAfasCode = (code) => {
    const newSelected = new Set(selectedAfasCodes);
    if (newSelected.has(code)) {
      newSelected.delete(code);
    } else {
      newSelected.add(code);
    }
    setSelectedAfasCodes(newSelected);
  };

  const toggleDomain = (domain) => {
    const newSelected = new Set(selectedDomains);
    if (newSelected.has(domain)) {
      newSelected.delete(domain);
    } else {
      newSelected.add(domain);
    }
    setSelectedDomains(newSelected);
  };

  const filteredAndSortedPlans = useMemo(() => {
    let filteredPlans = plans;

    if (selectedAfasCodes.size > 0) {
      filteredPlans = filteredPlans.filter(plan => selectedAfasCodes.has(plan.afas_artikelcode));
    }

    if (selectedDomains.size > 0) {
      filteredPlans = filteredPlans.filter(plan => selectedDomains.has(plan.domain_name));
    }

    if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase();
      filteredPlans = filteredPlans.filter(plan =>
        plan.domain_name?.toLowerCase().includes(lowerSearch) ||
        plan.sku_description?.toLowerCase().includes(lowerSearch)
      );
    }

    if (sortConfig.key) {
      filteredPlans = [...filteredPlans].sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return filteredPlans;
  }, [plans, searchTerm, sortConfig, selectedAfasCodes, selectedDomains]);

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = filteredAndSortedPlans.map(p => `${p.domain_name}-${p.sku_id}-${p.usage_start_time}`);
      setSelectedPlans(new Set(allIds));
    } else {
      setSelectedPlans(new Set());
    }
  };

  const handleSelectPlan = (planId) => {
    const newSelected = new Set(selectedPlans);
    if (newSelected.has(planId)) {
      newSelected.delete(planId);
    } else {
      newSelected.add(planId);
    }
    setSelectedPlans(newSelected);
  };

  const handleSelectDomainGroup = (domain, checked) => {
    const newSelected = new Set(selectedPlans);
    filteredAndSortedPlans.forEach(p => {
      if (p.domain_name === domain) {
        const id = `${p.domain_name}-${p.sku_id}-${p.usage_start_time}`;
        if (checked) {
          newSelected.add(id);
        } else {
          newSelected.delete(id);
        }
      }
    });
    setSelectedPlans(newSelected);
  };

  const isDomainGroupFullySelected = (domain) => {
    const domainPlans = filteredAndSortedPlans.filter(p => p.domain_name === domain);
    if (domainPlans.length === 0) return false;
    return domainPlans.every(p => selectedPlans.has(`${p.domain_name}-${p.sku_id}-${p.usage_start_time}`));
  };

  const executeSendToAfas = async (plansToSend) => {
    setShowConfirmModal(false);
    setSendingToAfas(true);
    setSuccessMessage('');
    try {
      await startJob('/api/jobs/afas-flexible-invoices', { month: selectedMonth, plans: plansToSend }, { t });
      setSuccessMessage(`Job triggered successfully for ${plansToSend.length} plans.`);
      setSelectedPlans(new Set());
    } catch (err) {
      // Not setError: that swaps the whole screen for the load-error view.
      toast.error(err.message);
    } finally {
      setSendingToAfas(false);
    }
  };

  const handleSendToAfas = () => {
    if (selectedPlans.size === 0) {
      setShowConfirmModal(true);
    } else {
      executeSendToAfas(plans.filter(p => selectedPlans.has(`${p.domain_name}-${p.sku_id}-${p.usage_start_time}`)));
    }
  };

  if (loading) {
    return (
      <div className="p-6 animate-pulse">
        <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
          <div>
            <div className="h-8 bg-gray-200 rounded w-64 mb-2"></div>
            <div className="h-4 bg-gray-200 rounded w-96"></div>
          </div>
          <div className="flex items-center space-x-4">
            <div className="h-10 bg-gray-200 rounded w-32"></div>
            <div className="h-10 bg-gray-200 rounded w-32"></div>
            <div className="h-10 bg-gray-200 rounded w-64"></div>
          </div>
        </div>
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-primary opacity-85">
              <tr>
                {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                  <th key={i} className="px-6 py-4"><div className="h-3 bg-white/40 rounded w-full"></div></th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {[1, 2, 3, 4, 5].map((rowIdx) => (
                <tr key={rowIdx}>
                  <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-4"></div></td>
                  <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-32"></div></td>
                  <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-16"></div></td>
                  <td className="px-6 py-4">
                    <div className="h-4 bg-gray-200 rounded w-48 mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded w-24"></div>
                  </td>
                  <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-32"></div></td>
                  <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-12 ml-auto"></div></td>
                  <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-20 ml-auto"></div></td>
                  <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-24 ml-auto"></div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 p-4 rounded-md">
        <h3 className="text-red-800 font-medium">Error Loading Data</h3>
        <p className="text-red-600 mt-1">{error}</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      <ReadOnlyNotice t={t} />
      <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
        <div className="flex items-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 mr-3 bg-primary text-white rounded-full p-1.5 flex-shrink-0 shadow-sm" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
          <h2 className="text-2xl font-bold text-gray-900">Flexible Plans</h2>
        </div>
        <div className="flex items-center space-x-4">
          <input
            type="month"
            className="border border-gray-300 rounded-md px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary text-gray-700"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
          />
          {canWrite && <button
            onClick={handleSendToAfas}
            disabled={sendingToAfas || filteredAndSortedPlans.length === 0}
            className={`px-4 py-2 rounded-md text-sm font-medium text-white transition-colors ${sendingToAfas || filteredAndSortedPlans.length === 0
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-primary hover:bg-opacity-90 shadow-sm'
              }`}
          >
            {sendingToAfas ? 'Sending...' : 'Send to AFAS'}
          </button>}
          
          <button
            onClick={() => setShowFiltersModal(true)}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            Filters
            {(selectedAfasCodes.size > 0 || selectedDomains.size > 0) && (
              <span className="bg-primary text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center ml-1">
                {selectedAfasCodes.size + selectedDomains.size}
              </span>
            )}
          </button>

          <input
            type="text"
            placeholder="Search domain or product..."
            className="border border-gray-300 rounded-md px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary w-64"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <div className="bg-indigo-50 text-indigo-700 px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap">
            Total Records: {filteredAndSortedPlans.length}
          </div>
        </div>
      </div>

      {/* Filters modal moved below */}

      {successMessage && (
        <div className="mb-4 bg-green-50 p-4 rounded-md">
          <p className="text-green-800 text-sm font-medium">{successMessage}</p>
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-primary text-white">
            <tr>
              {/* Selection only feeds Send to AFAS, which is hidden without
                  adminWrite. The cells stay so the columns keep lining up;
                  only the inputs go. */}
              <th scope="col" className="px-6 py-3 text-left">
                {canWrite && <input
                  type="checkbox"
                  className="rounded border-gray-300 text-indigo-600 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                  onChange={handleSelectAll}
                  checked={filteredAndSortedPlans.length > 0 && selectedPlans.size === filteredAndSortedPlans.length}
                />}
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-opacity-90" onClick={() => handleSort('domain_name')}>
                Domain {sortConfig.key === 'domain_name' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-opacity-90" onClick={() => handleSort('debtor_id')}>
                Debtor ID {sortConfig.key === 'debtor_id' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-opacity-90" onClick={() => handleSort('sku_description')}>
                Description {sortConfig.key === 'sku_description' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </th>
              <th scope="col" className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-opacity-90" onClick={() => handleSort('afas_artikelcode')}>
                AFAS Article Code {sortConfig.key === 'afas_artikelcode' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </th>
              <th scope="col" className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-opacity-90" onClick={() => handleSort('unit_price')}>
                Unit Price {sortConfig.key === 'unit_price' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </th>
              <th scope="col" className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-opacity-90" onClick={() => handleSort('total_price')}>
                Total Invoice {sortConfig.key === 'total_price' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </th>
              <th scope="col" className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-opacity-90" onClick={() => handleSort('bq_order_id')}>
                BQ Order ID &<br />Created Order ID {sortConfig.key === 'bq_order_id' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredAndSortedPlans.map((plan, index) => {
              const planId = `${plan.domain_name}-${plan.sku_id}-${plan.usage_start_time}`;
              const showGroupHeader = sortConfig.key === 'domain_name' && 
                (index === 0 || plan.domain_name !== filteredAndSortedPlans[index - 1].domain_name);
              const isLastInGroup = sortConfig.key === 'domain_name' && 
                (index === filteredAndSortedPlans.length - 1 || plan.domain_name !== filteredAndSortedPlans[index + 1].domain_name);

              let subtotal = 0, vatPercentage = 0, vatAmount = 0, grandTotal = 0;
              if (isLastInGroup) {
                const domainPlans = filteredAndSortedPlans.filter(p => p.domain_name === plan.domain_name);
                subtotal = domainPlans.reduce((sum, p) => sum + (p.total_price || 0), 0);
                
                if (plan.administratie === ADMINISTRATIE_XEBIA_CLOUD_CONTROL) vatPercentage = 0;
                else if (plan.vatCode === '1a' || plan.vatCode === 'V03') vatPercentage = 0.21;
                else if (plan.vatCode === '3bS' || plan.vatCode === '3aS' || plan.vatCode === 'V07') vatPercentage = 0;
                
                vatAmount = subtotal * vatPercentage;
                grandTotal = subtotal + vatAmount;
              }

              return (
                <React.Fragment key={planId}>
                  {showGroupHeader && (
                    <tr className="bg-gray-100 border-y border-gray-200">
                      <td className="px-6 py-2">
                        {canWrite && <input
                          type="checkbox"
                          className="rounded border-gray-400 text-primary shadow-sm focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                          checked={isDomainGroupFullySelected(plan.domain_name)}
                          onChange={(e) => handleSelectDomainGroup(plan.domain_name, e.target.checked)}
                        />}
                      </td>
                      <td colSpan="7" className="px-6 py-2 text-sm font-bold text-gray-800">
                        Select all "{plan.domain_name}"
                      </td>
                    </tr>
                  )}
                  <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {canWrite && <input
                      type="checkbox"
                      className="rounded border-gray-300 text-indigo-600 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                      checked={selectedPlans.has(planId)}
                      onChange={() => handleSelectPlan(planId)}
                    />}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {plan.customer_id ? (
                      <Link 
                        to={`/admin/customers/${plan.customer_id}`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-indigo-600 hover:text-indigo-900 hover:underline transition-colors"
                      >
                        {plan.domain_name}
                      </Link>
                    ) : (
                      plan.domain_name
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {plan.debtor_id || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-normal text-sm text-gray-500">
                    <div className="min-w-[300px]">
                      <span className="font-medium text-gray-900">{plan.usage_amount}</span> {plan.sku_description} used from {new Date(plan.usage_start_time).toLocaleDateString()} - {new Date(plan.usage_end_time).toLocaleDateString()}, {plan.days_used || 0} days
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">
                    {plan.afas_artikelcode ? (
                      <span className="inline-flex items-center gap-1">
                        <span
                          className="text-xs font-mono text-primary bg-fuchsia-50 px-1.5 py-0.5 rounded border border-primary whitespace-nowrap cursor-pointer hover:bg-fuchsia-100 transition-colors"
                          onClick={() => setShowAfasPiP(true)}
                        >
                          {plan.afas_artikelcode}
                        </span>
                        {afasArticles.length > 0 && !afasArticles.some(a => a.ItemCode === plan.afas_artikelcode) && (
                          <span
                            className="inline-flex items-center justify-center bg-red-100 text-red-800 border border-red-200 rounded-full h-4 w-4 font-extrabold text-[10px] cursor-help"
                            title="Warning: This Article Code is not found in the AFAS Articles database."
                          >
                            !
                          </span>
                        )}
                      </span>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                    {plan.unit_price ? plan.unit_price.toFixed(2) : '0.00'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-right">
                    {plan.total_price ? plan.total_price.toFixed(2) : '0.00'} EUR
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-xs font-mono text-gray-500 text-right">
                    <div>{plan.bq_order_id || '-'}</div>
                    {plan.created_order_id && (
                      <div className="mt-1 flex items-center justify-end gap-1.5">
                        {plan.isCreditNote && (
                          <span 
                            className="inline-flex items-center justify-center w-4 h-4 bg-red-500 text-white rounded-full font-bold text-[10px] leading-none select-none"
                            title="Credit Note"
                          >
                            -
                          </span>
                        )}
                        <Link
                          to={`/admin/afas-transactions/${plan.created_order_id}`}
                          className="text-indigo-600 font-semibold hover:text-indigo-800 hover:underline transition-colors"
                        >
                          {plan.created_order_id}
                        </Link>
                      </div>
                    )}
                  </td>
                </tr>
                {isLastInGroup && (
                  <>
                    <tr className="bg-gray-50 border-t border-gray-200">
                      <td colSpan="6" className="px-6 py-2 text-right text-sm font-bold text-gray-500">
                        Subtotal
                      </td>
                      <td className="px-6 py-2 text-right text-sm font-bold text-gray-700">
                        {subtotal.toFixed(2)} EUR
                      </td>
                      <td></td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td colSpan="6" className="px-6 py-2 text-right text-sm font-bold text-gray-500">
                        VAT {plan.vatCode ? `(${plan.vatCode}, ${vatPercentage * 100}%)` : '(0%)'}
                      </td>
                      <td className="px-6 py-2 text-right text-sm font-bold text-gray-700">
                        {vatAmount.toFixed(2)} EUR
                      </td>
                      <td></td>
                    </tr>
                    <tr className="bg-blue-50/50 border-b-2 border-gray-300">
                      <td colSpan="6" className="px-6 py-3 text-right text-sm font-black text-gray-800 uppercase tracking-wider">
                        Total Invoice Amount
                      </td>
                      <td className="px-6 py-3 text-right text-sm font-black text-indigo-700">
                        {grandTotal.toFixed(2)} EUR
                      </td>
                      <td></td>
                    </tr>
                  </>
                )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>

        {filteredAndSortedPlans.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No flexible plan data found.
          </div>
        )}
      </div>

      {showAfasPiP && (
        <AfasArticlePiP
          articles={afasArticles}
          onClose={() => setShowAfasPiP(false)}
        />
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900 bg-opacity-50 transition-opacity">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 animate-in fade-in zoom-in-95 duration-200">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Process All Records?</h3>
            <p className="text-gray-600 mb-6 text-sm">
              No records selected. Are you sure you want to process all {filteredAndSortedPlans.length} records?
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => executeSendToAfas(filteredAndSortedPlans)}
                className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary shadow-sm transition-colors"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filters Modal */}
      {showFiltersModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900 bg-opacity-50 transition-opacity">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl p-6 max-h-[90vh] flex flex-col animate-in fade-in zoom-in-95 duration-200">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-900">Filter Plans</h3>
              <button onClick={() => setShowFiltersModal(false)} className="text-gray-400 hover:text-gray-500 focus:outline-none">
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            
            <div className="overflow-y-auto flex-1 pr-2 space-y-6">
              {uniqueAfasCodes.length > 0 && (
                <div>
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-sm font-semibold text-gray-700">AFAS Article Code:</span>
                    {selectedAfasCodes.size > 0 && (
                      <button
                        onClick={() => setSelectedAfasCodes(new Set())}
                        className="text-gray-400 hover:text-red-500 hover:bg-red-50 p-1 rounded-full transition-colors focus:outline-none"
                        title="Clear AFAS Filters"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {uniqueAfasCodes.map(code => {
                      const isSelected = selectedAfasCodes.has(code);
                      return (
                        <button
                          key={code}
                          onClick={() => toggleAfasCode(code)}
                          className={`px-3.5 py-1.5 rounded-full text-xs font-bold transition-all duration-200 border select-none ${
                            isSelected
                              ? 'bg-primary text-white border-primary shadow-sm shadow-primary/20 scale-105'
                              : 'bg-white text-primary border-primary/20 hover:bg-primary/5 hover:border-primary/40'
                          }`}
                        >
                          {code}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {uniqueDomains.length > 0 && (
                <div>
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-sm font-semibold text-gray-700">Domain Name:</span>
                    {selectedDomains.size > 0 && (
                      <button
                        onClick={() => setSelectedDomains(new Set())}
                        className="text-gray-400 hover:text-red-500 hover:bg-red-50 p-1 rounded-full transition-colors focus:outline-none"
                        title="Clear Domain Filters"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {uniqueDomains.map(domain => {
                      const isSelected = selectedDomains.has(domain);
                      return (
                        <button
                          key={domain}
                          onClick={() => toggleDomain(domain)}
                          className={`px-3.5 py-1.5 rounded-full text-xs font-bold transition-all duration-200 border select-none ${
                            isSelected
                              ? 'bg-teal-600 text-white border-teal-600 shadow-sm shadow-teal-600/20 scale-105'
                              : 'bg-white text-teal-700 border-teal-200 hover:bg-teal-50/50 hover:border-teal-300'
                          }`}
                        >
                          {domain}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <div className="mt-6 flex justify-end gap-3 pt-4 border-t border-gray-200">
              {(selectedAfasCodes.size > 0 || selectedDomains.size > 0) && (
                <button
                  onClick={() => { setSelectedAfasCodes(new Set()); setSelectedDomains(new Set()); }}
                  className="px-4 py-2 text-sm font-medium text-red-700 bg-white border border-red-200 rounded-md hover:bg-red-50 transition-colors"
                >
                  Clear All
                </button>
              )}
              <button
                onClick={() => setShowFiltersModal(false)}
                className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-opacity-90 transition-colors shadow-sm"
              >
                Close & Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminFlexiblePlans;
